import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const SafetySection = () => {
  const sectionRef = useRef(null);
  const textRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Text animation
      gsap.fromTo(textRef.current,
        {
          x: -100,
          opacity: 0
        },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: textRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        }
      );

      // Image animation
      gsap.fromTo(imageRef.current,
        {
          x: 100,
          opacity: 0,
          scale: 0.8
        },
        {
          x: 0,
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: imageRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        }
      );

      // Floating animation for image
      gsap.to(imageRef.current, {
        y: -20,
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const features = [
    { icon: "🎯", title: "Precision Design", description: "Exact measurements and detailed planning" },
    { icon: "🛡️", title: "Quality Assurance", description: "Rigorous quality checks at every stage" },
    { icon: "⚡", title: "Quick Installation", description: "Efficient and timely project completion" },
    { icon: "🌟", title: "Premium Materials", description: "Only the finest materials used" }
  ];

  const Navigate = useNavigate();

  const handleBookConsultation = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth' // Optional: adds smooth scrolling
  });
  Navigate('/contact');
};

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
      aria-labelledby="safety-heading"
    >
      {/* Background with gradient overlay */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(/assets/Images/12l.webp)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        {/* Enhanced gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-l from-orange-600/10 to-transparent"></div>
      </div>

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-4 h-4 bg-orange-400 rounded-full opacity-60 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-3 h-3 bg-amber-300 rounded-full opacity-40 animate-pulse delay-1000"></div>
        <div className="absolute bottom-40 left-20 w-2 h-2 bg-orange-300 rounded-full opacity-50 animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full py-20 px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Text Section */}
          <motion.article
            ref={textRef}
            className="text-white space-y-8"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div
              className="inline-flex items-center gap-2 bg-orange-500/20 backdrop-blur-sm border border-orange-500/30 px-4 py-2 rounded-full"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
            >
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              <span className="text-orange-300 text-sm font-semibold uppercase tracking-wide">
                Premium Service
              </span>
            </motion.div>

            {/* Main Heading */}
            <header className="space-y-4">
              <h2
                id="safety-heading"
                className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
              >
                Ensuring a{" "}
                <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
                  Safe & Perfect
                </span>{" "}
                Experience
              </h2>
              <p className="text-xl text-gray-200 leading-relaxed">
                From innovative designs to flawless installations, we ensure your 
                complete satisfaction and peace of mind at every step.
              </p>
            </header>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/10 hover:border-orange-400/30 transition-all duration-300 group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl group-hover:scale-110 transition-transform duration-300">
                      {feature.icon}
                    </span>
                    <div>
                      <h3 className="font-semibold text-white text-lg">
                        {feature.title}
                      </h3>
                      <p className="text-gray-300 text-sm mt-1">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <motion.div 
  className="flex flex-col sm:flex-row gap-4 pt-4"
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.6 }}
>
  <motion.button
    onClick={handleBookConsultation}
    className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-2xl hover:shadow-orange-500/25 transition-all duration-300 flex items-center gap-2 group"
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    aria-label="Get Free Kitchen Design Consultation"
  >
    <span className="flex items-center gap-2">
      Get Free Consultation
      <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
      </svg>
    </span>
  </motion.button>
  
</motion.div>

            {/* Trust Indicators */}
           
          </motion.article>

          {/* Image Section */}
          <motion.div
            ref={imageRef}
            className="relative flex justify-center items-center"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative">
              {/* Main Image */}
              <motion.img
                src="/assets/Images/11l.webp"
                alt="Professional kitchen design and installation process"
                className="relative z-10 rounded-3xl shadow-2xl w-full max-w-md lg:max-w-lg border-4 border-white/20 backdrop-blur-sm"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
              
              {/* Floating Elements */}
              <motion.div
                className="absolute -top-4 -right-4 bg-gradient-to-br from-orange-500 to-amber-500 text-white p-4 rounded-2xl shadow-2xl z-20"
                initial={{ scale: 0, rotate: -10 }}
                whileInView={{ scale: 1, rotate: 0 }}
                transition={{ delay: 1, type: "spring" }}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold">★ 5/5</div>
                  <div className="text-xs">Customer Rating</div>
                </div>
              </motion.div>

              <motion.div
                className="absolute -bottom-4 -left-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white p-3 rounded-xl shadow-2xl z-20"
                initial={{ scale: 0, y: 20 }}
                whileInView={{ scale: 1, y: 0 }}
                transition={{ delay: 1.2, type: "spring" }}
              >
                <div className="text-center">
                  <div className="font-semibold">Quick</div>
                  <div className="text-xs">Installation</div>
                </div>
              </motion.div>

              {/* Background Decorative Element */}
              <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/20 to-amber-500/20 rounded-3xl blur-xl -z-10"></div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent z-0"></div>
    </section>
  );
};

export default SafetySection;













// import { useEffect, useRef } from "react";
// import { gsap } from "gsap";
// import { ScrollTrigger } from "gsap/ScrollTrigger";
// import { motion } from "framer-motion";
// import { useNavigate } from "react-router-dom";
// import "./theme.css"; // Import the CSS file

// gsap.registerPlugin(ScrollTrigger);

// const SafetySection = () => {
//   const sectionRef = useRef(null);
//   const textRef = useRef(null);
//   const imageRef = useRef(null);

//   useEffect(() => {
//     const ctx = gsap.context(() => {
//       // Text animation
//       gsap.fromTo(textRef.current,
//         {
//           x: -100,
//           opacity: 0
//         },
//         {
//           x: 0,
//           opacity: 1,
//           duration: 1,
//           ease: "power3.out",
//           scrollTrigger: {
//             trigger: textRef.current,
//             start: "top 80%",
//             toggleActions: "play none none reverse"
//           }
//         }
//       );

//       // Image animation
//       gsap.fromTo(imageRef.current,
//         {
//           x: 100,
//           opacity: 0,
//           scale: 0.8
//         },
//         {
//           x: 0,
//           opacity: 1,
//           scale: 1,
//           duration: 1,
//           ease: "power3.out",
//           scrollTrigger: {
//             trigger: imageRef.current,
//             start: "top 80%",
//             toggleActions: "play none none reverse"
//           }
//         }
//       );

//       // Floating animation for image
//       gsap.to(imageRef.current, {
//         y: -20,
//         duration: 2,
//         repeat: -1,
//         yoyo: true,
//         ease: "power1.inOut"
//       });

//     }, sectionRef);

//     return () => ctx.revert();
//   }, []);

//   const features = [
//     { icon: "🎯", title: "Precision Design", description: "Exact measurements and detailed planning" },
//     { icon: "🛡️", title: "Quality Assurance", description: "Rigorous quality checks at every stage" },
//     { icon: "⚡", title: "Quick Installation", description: "Efficient and timely project completion" },
//     { icon: "🌟", title: "Premium Materials", description: "Only the finest materials used" }
//   ];

//   const Navigate = useNavigate();

//   const handleBookConsultation = () => {
//     window.scrollTo({
//       top: 0,
//       left: 0,
//       behavior: 'smooth'
//     });
//     Navigate('/contact');
//   };

//   return (
//     <section
//       ref={sectionRef}
//       className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
//       aria-labelledby="safety-heading"
//     >
//       {/* Background with gradient overlay */}
//       <div 
//         className="absolute inset-0 z-0"
//         style={{
//           backgroundImage: `url(/assets/Images/12l.webp)`,
//           backgroundSize: "cover",
//           backgroundPosition: "center",
//           backgroundAttachment: "fixed",
//         }}
//       >
//         {/* Enhanced gradient overlays */}
//         <div 
//           className="absolute inset-0"
//           style={{
//             background: "var(--overlay-dark-gradient)",
//           }}
//         ></div>
//         <div 
//           className="absolute inset-0"
//           style={{
//             background: "var(--overlay-vertical-gradient)",
//           }}
//         ></div>
//         <div 
//           className="absolute inset-0"
//           style={{
//             background: "var(--overlay-primary-light)",
//           }}
//         ></div>
//       </div>

//       {/* Animated background elements */}
//       <div className="absolute inset-0 overflow-hidden">
//         <div 
//           className="absolute top-20 left-10 w-4 h-4 rounded-full opacity-60 animate-pulse"
//           style={{
//             backgroundColor: "var(--color-primary-400)",
//           }}
//         ></div>
//         <div 
//           className="absolute top-40 right-20 w-3 h-3 rounded-full opacity-40 animate-pulse delay-1000"
//           style={{
//             backgroundColor: "var(--color-amber-300)",
//           }}
//         ></div>
//         <div 
//           className="absolute bottom-40 left-20 w-2 h-2 rounded-full opacity-50 animate-pulse delay-500"
//           style={{
//             backgroundColor: "var(--color-primary-300)",
//           }}
//         ></div>
//       </div>

//       <div className="relative z-10 max-w-7xl mx-auto w-full py-20 px-6 md:px-10 lg:px-16">
//         <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
//           {/* Text Section */}
//           <motion.article
//             ref={textRef}
//             className="space-y-8"
//             initial={{ opacity: 0, x: -50 }}
//             whileInView={{ opacity: 1, x: 0 }}
//             transition={{ duration: 0.8 }}
//             style={{
//               color: "var(--color-white)",
//             }}
//           >
//             {/* Badge */}
//             <motion.div
//               className="inline-flex items-center gap-2 backdrop-blur-sm px-4 py-2 rounded-full"
//               initial={{ scale: 0 }}
//               whileInView={{ scale: 1 }}
//               transition={{ type: "spring", stiffness: 200, damping: 15 }}
//               style={{
//                 backgroundColor: "var(--bg-primary-500-20)",
//                 border: "1px solid var(--border-primary-30)",
//               }}
//             >
//               <div 
//                 className="w-2 h-2 rounded-full animate-pulse"
//                 style={{
//                   backgroundColor: "var(--color-primary-400)",
//                 }}
//               ></div>
//               <span 
//                 className="text-sm font-semibold uppercase tracking-wide"
//                 style={{
//                   color: "var(--color-amber-300)",
//                 }}
//               >
//                 Premium Service
//               </span>
//             </motion.div>

//             {/* Main Heading */}
//             <header className="space-y-4">
//               <h2
//                 id="safety-heading"
//                 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
//                 style={{
//                   color: "var(--color-white)",
//                 }}
//               >
//                 Ensuring a{" "}
//                 <span 
//                   className="bg-clip-text text-transparent"
//                   style={{
//                     background: "var(--gradient-primary)",
//                     WebkitBackgroundClip: "text",
//                     backgroundClip: "text",
//                   }}
//                 >
//                   Safe & Perfect
//                 </span>{" "}
//                 Experience
//               </h2>
//               <p 
//                 className="text-xl leading-relaxed"
//                 style={{
//                   color: "var(--color-gray-200)",
//                 }}
//               >
//                 From innovative designs to flawless installations, we ensure your 
//                 complete satisfaction and peace of mind at every step.
//               </p>
//             </header>

//             {/* Features Grid */}
//             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//               {features.map((feature, index) => (
//                 <motion.div
//                   key={index}
//                   className="backdrop-blur-sm rounded-2xl p-4 transition-all duration-300 group"
//                   initial={{ opacity: 0, y: 20 }}
//                   whileInView={{ opacity: 1, y: 0 }}
//                   transition={{ delay: index * 0.1 + 0.3 }}
//                   whileHover={{ scale: 1.02 }}
//                   style={{
//                     backgroundColor: "var(--bg-white-10)",
//                     border: "1px solid var(--border-white-10)",
//                   }}
//                 >
//                   <div className="flex items-start gap-3">
//                     <span className="text-2xl group-hover:scale-110 transition-transform duration-300">
//                       {feature.icon}
//                     </span>
//                     <div>
//                       <h3 
//                         className="font-semibold text-lg"
//                         style={{
//                           color: "var(--color-white)",
//                         }}
//                       >
//                         {feature.title}
//                       </h3>
//                       <p 
//                         className="text-sm mt-1"
//                         style={{
//                           color: "var(--color-gray-300)",
//                         }}
//                       >
//                         {feature.description}
//                       </p>
//                     </div>
//                   </div>
//                 </motion.div>
//               ))}
//             </div>

//             {/* CTA Buttons */}
//             <motion.div 
//               className="flex flex-col sm:flex-row gap-4 pt-4"
//               initial={{ opacity: 0, y: 20 }}
//               whileInView={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.6 }}
//             >
//               <motion.button
//                 onClick={handleBookConsultation}
//                 className="text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 group"
//                 whileHover={{ scale: 1.05 }}
//                 whileTap={{ scale: 0.95 }}
//                 aria-label="Get Free Kitchen Design Consultation"
//                 style={{
//                   background: "var(--gradient-primary)",
//                 }}
//               >
//                 <span className="flex items-center gap-2">
//                   Get Free Consultation
//                   <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
//                   </svg>
//                 </span>
//               </motion.button>
//             </motion.div>
//           </motion.article>

//           {/* Image Section */}
//           <motion.div
//             ref={imageRef}
//             className="relative flex justify-center items-center"
//             initial={{ opacity: 0, x: 50 }}
//             whileInView={{ opacity: 1, x: 0 }}
//             transition={{ duration: 0.8, delay: 0.2 }}
//           >
//             <div className="relative">
//               {/* Main Image */}
//               <motion.img
//                 src="/assets/Images/11l.webp"
//                 alt="Professional kitchen design and installation process"
//                 className="relative z-10 rounded-3xl w-full max-w-md lg:max-w-lg backdrop-blur-sm"
//                 whileHover={{ scale: 1.02 }}
//                 transition={{ type: "spring", stiffness: 300 }}
//                 style={{
//                   border: "4px solid var(--border-white-10)",
//                   boxShadow: "var(--shadow-large)",
//                 }}
//               />
              
//               {/* Floating Elements */}
//               <motion.div
//                 className="absolute -top-4 -right-4 text-white p-4 rounded-2xl z-20"
//                 initial={{ scale: 0, rotate: -10 }}
//                 whileInView={{ scale: 1, rotate: 0 }}
//                 transition={{ delay: 1, type: "spring" }}
//                 style={{
//                   background: "var(--gradient-primary)",
//                   boxShadow: "var(--shadow-large)",
//                 }}
//               >
//                 <div className="text-center">
//                   <div className="text-2xl font-bold">★ 5/5</div>
//                   <div className="text-xs">Customer Rating</div>
//                 </div>
//               </motion.div>

//               <motion.div
//                 className="absolute -bottom-4 -left-4 backdrop-blur-sm text-white p-3 rounded-xl z-20"
//                 initial={{ scale: 0, y: 20 }}
//                 whileInView={{ scale: 1, y: 0 }}
//                 transition={{ delay: 1.2, type: "spring" }}
//                 style={{
//                   backgroundColor: "var(--overlay-white-10)",
//                   border: "1px solid var(--border-white-20)",
//                   boxShadow: "var(--shadow-large)",
//                 }}
//               >
//                 <div className="text-center">
//                   <div className="font-semibold">Quick</div>
//                   <div className="text-xs">Installation</div>
//                 </div>
//               </motion.div>

//               {/* Background Decorative Element */}
//               <div 
//                 className="absolute -inset-4 rounded-3xl blur-xl -z-10"
//                 style={{
//                   background: "linear-gradient(to right, var(--color-primary-500), var(--color-amber-500), var(--color-primary-500))",
//                   opacity: 0.2,
//                 }}
//               ></div>
//             </div>
//           </motion.div>
//         </div>
//       </div>

//       {/* Bottom Gradient */}
//       <div 
//         className="absolute bottom-0 left-0 right-0 h-32 z-0"
//         style={{
//           background: "linear-gradient(to top, var(--color-black) 0%, transparent 100%)",
//         }}
//       ></div>
//     </section>
//   );
// };

// export default SafetySection;